import React, { useState, useEffect } from 'react';
import { Share2, Plus, Check, X, Edit2, Trash2, RefreshCw, Image, Calendar, ExternalLink } from 'lucide-react';
import { adminAPI } from '../lib/api';
import { Modal, LoadingPage, EmptyState } from '../components/shared';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  draft:     { bg: 'rgba(255,255,255,0.05)', color: 'var(--text-muted)',  label: 'Draft' },
  approved:  { bg: 'rgba(201,162,39,0.1)',  color: 'var(--gold)',        label: 'Approved' },
  published: { bg: 'rgba(42,157,143,0.1)',  color: 'var(--teal)',        label: 'Published' },
  failed:    { bg: 'rgba(239,68,68,0.1)',   color: 'var(--error)',       label: 'Failed' },
  rejected:  { bg: 'rgba(239,68,68,0.05)', color: 'var(--text-muted)',  label: 'Rejected' },
};

const TRIGGER_LABELS = {
  proactive:             'Proactive',
  topic_approved:        'Topic Approved',
  sponsorship_confirmed: 'Sponsorship',
  deadline_approaching:  'Deadline',
  finale_countdown:      'Finale Countdown',
};

export default function AdminSocialPage() {
  const [posts, setPosts]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('draft');
  const [selected, setSelected]         = useState(null);
  const [editContent, setEditContent]   = useState('');
  const [scheduledAt, setScheduledAt]   = useState('');
  const [generating, setGenerating]     = useState(false);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [approving, setApproving]       = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = React.useRef(null);

  useEffect(() => {
    adminAPI.getLinkedInStatus()
      .then(r => setLinkedinStatus(r.data))
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    adminAPI.getSocialPosts({ status: filter })
      .then(r => setPosts(r.data.posts || []))
      .catch(() => toast.error('Failed to load posts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await adminAPI.generateSocialPost({ platform: 'linkedin', type: 'proactive' });
      toast.success('Draft generated!');
      setFilter('draft');
      load();
    } catch {
      toast.error('Generation failed. Check API key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (post) => {
    setApproving(post.id);
    try {
      await adminAPI.approveSocialPost(post.id, { content: editContent || post.content, scheduled_at: scheduledAt || null });
      toast.success('Post approved!');
      setSelected(null);
      load();
    } catch {
      toast.error('Failed to approve.');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this post?')) return;
    try {
      await adminAPI.rejectSocialPost(id);
      toast.success('Post rejected.');
      load();
    } catch {
      toast.error('Failed to reject.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await adminAPI.deleteSocialPost(id);
      toast.success('Post deleted.');
      load();
    } catch {
      toast.error('Failed to delete.');
    }
  };

  const handleImageUpload = async (postId, file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await adminAPI.uploadSocialImage(postId, formData);
      toast.success('Image uploaded.');
      load();
    } catch {
      toast.error('Failed to upload image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const openEdit = (post) => {
    setSelected(post);
    setEditContent(post.content);
    setScheduledAt(post.scheduled_at ? post.scheduled_at.slice(0,16) : '');
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Social Media Agent</h1>
          <p className="page-subtitle">AI-generated posts for the Futurum LinkedIn company page</p>
        </div>
        <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
          {generating ? <><RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</> : <><Plus size={15} /> Generate Post</>}
        </button>
      </div>

      {/* LinkedIn Status Bar */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderRadius:10, background: linkedinStatus?.connected ? 'rgba(42,157,143,0.08)' : 'rgba(239,68,68,0.06)', border:'1px solid ' + (linkedinStatus?.connected ? 'rgba(42,157,143,0.2)' : 'rgba(239,68,68,0.15)'), marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background: linkedinStatus?.connected ? 'var(--teal)' : '#ef4444' }} />
          <span style={{ fontSize:'0.85rem', color: linkedinStatus?.connected ? 'var(--teal)' : '#ef4444' }}>
            {linkedinStatus === null ? 'Checking LinkedIn connection…' : linkedinStatus.connected ? 'LinkedIn company page connected' : 'LinkedIn not connected — Community Management API approval pending'}
          </span>
        </div>
        {!linkedinStatus?.connected && (
          <a href="/api/auth/linkedin" className="btn btn-ghost" style={{ fontSize:'0.8rem', padding:'6px 14px' }}>
            Connect LinkedIn →
          </a>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 0 }}>
        {['draft','approved','published','rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{ padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: filter === s ? 'var(--gold)' : 'var(--text-muted)', borderBottom: filter === s ? '2px solid var(--gold)' : '2px solid transparent', textTransform: 'capitalize' }}>
            {s}
          </button>
        ))}
      </div>

      {loading ? <LoadingPage /> : posts.length === 0 ? (
        <EmptyState icon={Share2} title={'No ' + filter + ' posts'} description="Generate a new post using the button above." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {posts.map(post => (
            <div key={post.id} className="card" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 4, background: STATUS_COLORS[post.status]?.bg, color: STATUS_COLORS[post.status]?.color }}>
                      {STATUS_COLORS[post.status]?.label || post.status}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 4 }}>
                      {TRIGGER_LABELS[post.trigger_type] || post.trigger_type}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {post.platform}
                    </span>
                    {post.scheduled_at && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={11} /> {new Date(post.scheduled_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {post.content}
                  </p>
                  {post.image_path && (
                    <div style={{ marginTop: 10 }}>
                      <img src={post.image_path} alt="Post image" style={{ height: 80, borderRadius: 6, objectFit: 'cover' }} />
                    </div>
                  )}
                  <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {new Date(post.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
                  {post.status === 'draft' && (
                    <>
                      <button className="btn btn-primary" style={{ fontSize: '0.8rem', padding: '6px 14px' }} onClick={() => openEdit(post)}>
                        <Edit2 size={13} /> Review & Approve
                      </button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '6px 12px', color: 'var(--error)' }} onClick={() => handleReject(post.id)}>
                        <X size={13} /> Reject
                      </button>
                    </>
                  )}
                  {post.status === 'approved' && (
                    <span style={{ fontSize: '0.78rem', color: 'var(--gold)' }}>Scheduled for publishing</span>
                  )}
                  {post.status === 'published' && post.linkedin_post_id && (
                    <a href={'https://www.linkedin.com/feed/update/' + post.linkedin_post_id} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{ fontSize: '0.8rem' }}>
                      <ExternalLink size={13} /> View on LinkedIn
                    </a>
                  )}
                  <button className="btn btn-ghost" style={{ fontSize: '0.8rem', padding: '6px 12px', color: 'var(--error)' }} onClick={() => handleDelete(post.id)}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit & Approve Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="Review & Approve Post" size="lg">
        {selected && (
          <div style={{ padding: '0 4px' }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>POST COPY</label>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={12}
                style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 14, color: 'var(--text-primary)', fontSize: '0.875rem', lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{editContent.length} characters</div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>IMAGE</label>
              {selected.image_path && <img src={selected.image_path} alt="Post" style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />}
              <input ref={imageInputRef} type="file" accept=".png,.jpg,.jpeg" style={{ display: 'none' }} onChange={e => e.target.files && e.target.files[0] && handleImageUpload(selected.id, e.target.files[0])} />
              <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} disabled={uploadingImage} onClick={() => imageInputRef.current && imageInputRef.current.click()}>
                <Image size={13} /> {uploadingImage ? 'Uploading...' : selected.image_path ? 'Replace Image' : 'Upload Image'}
              </button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>SCHEDULE (optional)</label>
              <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '10px 14px', color: 'var(--text-primary)', fontSize: '0.875rem', width: '100%' }} />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Leave blank to publish immediately when you're ready</div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setSelected(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => handleApprove(selected)} disabled={approving === selected.id}>
                <Check size={14} /> {approving === selected.id ? 'Approving…' : 'Approve Post'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
